import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Label } from "@gigpro/ui/Label";
import type { WorkedWithYouPro } from "@sidegigllc/api/client";
import { ProAvatar } from "domains/pros/components/Avatar";
import React from "react";

type JobLiveStepProps = {
  jobName: string;
  /** Already limited to what should be shown. */
  pros: ReadonlyArray<WorkedWithYouPro>;
  /** Ids of pros an offer went to during this step. */
  offeredProIds: ReadonlySet<number>;
  /** Whether the invite option is offered at all. */
  canInvite: boolean;
  inviteChecked: boolean;
  onInviteCheckedChange: (checked: boolean) => void;
  onOffer: (pro: WorkedWithYouPro) => void;
  onDone: () => void;
  isSubmitting?: boolean;
  /** The Jobs plan card, shown where the invite option would be while the location can't reach Pros yet. */
  trialCard?: React.ReactNode;
};

/** A Pro who qualifies only as a Favorite has no position shifts, so say why they're listed instead of "0 shifts". */
const summarizeHistory = ({ history }: WorkedWithYouPro): string => {
  const { shifts_in_position, average_rating, is_favorite } = history;
  const parts: Array<string> = [];
  if (shifts_in_position > 0)
    parts.push(`${shifts_in_position} ${shifts_in_position === 1 ? "shift" : "shifts"} with you`);
  if (average_rating != null) parts.push(`${average_rating.toFixed(1)} avg rating`);
  if (shifts_in_position === 0 && is_favorite) parts.push("Favorite");
  return parts.join(" · ");
};

export const JobLiveStep: React.FC<JobLiveStepProps> = ({
  jobName,
  pros,
  offeredProIds,
  canInvite,
  inviteChecked,
  onInviteCheckedChange,
  onOffer,
  onDone,
  isSubmitting,
  trialCard,
}) => (
  <div className="space-y-4">
    <div>
      <h3 className="text-large font-semibold">Your job is live</h3>
      <p className="text-gray">{jobName} is now open for applications.</p>
    </div>
    {pros.length > 0 && (
      <section aria-labelledby="job-live-pros-heading" className="space-y-2">
        <h4 id="job-live-pros-heading" className="font-semibold">
          Pros who've worked with you
        </h4>
        <p className="text-small font-regular text-gray">
          Send an offer to hire one of them directly. They'll be asked to accept in the app.
        </p>
        <ul className="flex flex-col gap-2">
          {pros.map((pro) => {
            const name = `${pro.pro.first_name} ${pro.pro.last_name}`;
            const offered = offeredProIds.has(pro.pro.id);
            return (
              <li key={pro.pro.id} className="flex items-center gap-2">
                <ProAvatar imageId={pro.pro.image_public_url} displayName={name} size={40} disableZoom />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{name}</div>
                  <div className="truncate text-small text-gray">{summarizeHistory(pro)}</div>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={offered}
                  onClick={() => onOffer(pro)}
                  aria-label={`Send offer to ${name}`}
                >
                  {offered ? "Offer sent" : "Send offer"}
                </Button>
              </li>
            );
          })}
        </ul>
      </section>
    )}
    {trialCard}
    {canInvite && (
      <div className="flex items-start gap-2">
        <Checkbox
          id="job-live-invite"
          checked={inviteChecked}
          onCheckedChange={(checked) => onInviteCheckedChange(checked === true)}
        />
        <Label htmlFor="job-live-invite" className="flex flex-col gap-0.5">
          <span>Invite pros who've worked with you to apply</span>
          <span className="text-small font-regular text-gray">
            They'll get a notification about this job. Their contact details stay private until they accept or apply.
          </span>
        </Label>
      </div>
    )}
    <div className="flex justify-end">
      <Button onClick={onDone} loading={isSubmitting}>
        Done
      </Button>
    </div>
  </div>
);
